// Recordings — the agent-facing call-recordings portal.
//
// An agent sees ONLY the recordings they personally handled; supervisors /
// QA leads / admins see the whole team's. That scope is enforced server-side
// by the Firestore security rules (see firestore.rules) — this page can only
// ever render what the rules already permit. Playback goes through the
// generateRecordingUrl callable, which audits every access and returns a
// short-lived presigned URL (the audio never lives in the app).
//
// Search UX — two layers that the user never has to think about:
//
//   1. Browse window: a working set (scope + optional date range, newest
//      first) loaded in chunks, filtered LIVE in memory as the user types —
//      case-insensitive partial matches, no "apply" step. "Load older" pages
//      further back.
//   2. Global identifier search: the moment the user types a Call ID, Caller #,
//      or Agent, we ALSO run server-side prefix queries over their FULL scoped
//      history (debounced) and merge the hits in. Pasting a call ID finds the
//      call — no dates, no paging, regardless of how old it is.
//
// All active filters AND together over the merged pool, so the two layers are
// invisible: it just behaves like searching everything.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IonContent, IonPage, IonSpinner } from '@ionic/react';
import { useHistory } from 'react-router-dom';

import { getCurrentUser, readUser } from '../firebase';
import {
  loadRecordingsWorkingSet,
  requestPlaybackUrl,
  searchRecordingsGlobal,
} from '../recordings/firebase';
import {
  Recording,
  RecordingsRole,
  resolveRecordingsRole,
  isElevatedRecordingsRole,
} from '../recordings/types';
import {
  RECORDINGS_GLOBAL_SEARCH_MIN_CHARS,
  RECORDINGS_ROLE_LABELS,
  directionLabel,
  maskPhone,
} from '../recordings/constants';

// Convert a yyyy-mm-dd input value to an epoch-ms bound. `endOfDay` pushes to
// 23:59:59.999 so a date_to range is inclusive of the whole day.
function dateInputToMs(v: string, endOfDay = false): number | undefined {
  if (!v) return undefined;
  const ms = Date.parse(endOfDay ? `${v}T23:59:59.999` : `${v}T00:00:00.000`);
  return Number.isNaN(ms) ? undefined : ms;
}

// Render a recording's timestamp in the viewer's local timezone, with the zone
// abbreviation shown so it's unambiguous. Falls back to Pacific if the browser
// can't resolve a timezone (Rollover Rep operates in Pacific).
function fmtWhen(ms: number): string {
  if (!ms) return '—';
  try {
    return new Date(ms).toLocaleString(undefined, { timeZoneName: 'short' });
  } catch {
    try {
      return new Date(ms).toLocaleString('en-US', {
        timeZone: 'America/Los_Angeles',
        timeZoneName: 'short',
      });
    } catch {
      return '—';
    }
  }
}

// Case-insensitive partial match. Empty query matches everything.
function includesCI(value: string | null | undefined, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return String(value ?? '').toLowerCase().includes(q);
}

type Access =
  | { kind: 'loading' }
  | { kind: 'denied' }
  | { kind: 'ok'; role: RecordingsRole; email: string };

const Recordings: React.FC = () => {
  const history = useHistory();
  const [access, setAccess] = useState<Access>({ kind: 'loading' });

  // Live search inputs. Text boxes + direction filter client-side; the date
  // range bounds the server fetch.
  const [callId, setCallId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [direction, setDirection] = useState('');
  const [disposition, setDisposition] = useState('');
  const [campaign, setCampaign] = useState('');
  const [fromNumber, setFromNumber] = useState('');
  const [agentQuery, setAgentQuery] = useState(''); // elevated only

  const [workingSet, setWorkingSet] = useState<Recording[]>([]);
  const [capped, setCapped] = useState(false);
  const [loadState, setLoadState] = useState<'idle' | 'loading' | 'error'>('loading');
  const [loadingMore, setLoadingMore] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  // Cursor for "Load older" (opaque Firestore snapshot; not render state).
  const pageCursorRef = useRef<any>(null);

  // Whole-history identifier hits, merged into the visible list. `searching`
  // drives the "searching all recordings…" status.
  const [globalHits, setGlobalHits] = useState<Recording[]>([]);
  const [searching, setSearching] = useState(false);
  const searchSeqRef = useRef(0);

  // Currently-playing recording id + its presigned URL.
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [playUrl, setPlayUrl] = useState<string | null>(null);
  const [playBusyId, setPlayBusyId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Resolve the caller's recordings role on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const u: any = await getCurrentUser();
        if (!u) return !cancelled && setAccess({ kind: 'denied' });
        const data = await readUser(u.uid);
        const role = resolveRecordingsRole(data && data.restricted);
        if (cancelled) return;
        if (!role) setAccess({ kind: 'denied' });
        else setAccess({ kind: 'ok', role, email: (u.email || '').toLowerCase() });
      } catch {
        if (!cancelled) setAccess({ kind: 'denied' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch the first chunk of the browse window. Re-runs when access resolves
  // or the date range changes (single-sided ranges are fine — either bound
  // alone narrows the window).
  const loadSet = useCallback(async () => {
    if (access.kind !== 'ok') return;
    setLoadState('loading');
    setErrorMsg('');
    pageCursorRef.current = null;
    try {
      const { items, capped: wasCapped, cursor } = await loadRecordingsWorkingSet({
        role: access.role,
        email: access.email,
        dateFrom: dateInputToMs(dateFrom),
        dateTo: dateInputToMs(dateTo, true),
      });
      setWorkingSet(items);
      setCapped(wasCapped);
      pageCursorRef.current = cursor ?? null;
      setLoadState('idle');
    } catch (e: any) {
      setLoadState('error');
      setErrorMsg(
        (e && typeof e.message === 'string' && e.message) ||
          'Could not load recordings. Please try again.',
      );
    }
  }, [access, dateFrom, dateTo]);

  useEffect(() => {
    loadSet();
  }, [loadSet]);

  // Append the next-older chunk to the browse window.
  const loadMore = useCallback(async () => {
    if (access.kind !== 'ok' || !pageCursorRef.current || loadingMore) return;
    setLoadingMore(true);
    try {
      const { items, capped: wasCapped, cursor } = await loadRecordingsWorkingSet({
        role: access.role,
        email: access.email,
        dateFrom: dateInputToMs(dateFrom),
        dateTo: dateInputToMs(dateTo, true),
        cursor: pageCursorRef.current,
      });
      setWorkingSet((prev) => {
        const seen = new Set(prev.map((r) => r.id));
        return [...prev, ...items.filter((r) => !seen.has(r.id))];
      });
      setCapped(wasCapped);
      pageCursorRef.current = cursor ?? null;
    } catch (e: any) {
      setErrorMsg(
        (e && typeof e.message === 'string' && e.message) ||
          'Could not load older recordings. Please try again.',
      );
    } finally {
      setLoadingMore(false);
    }
  }, [access, dateFrom, dateTo, loadingMore]);

  const elevated = access.kind === 'ok' && isElevatedRecordingsRole(access.role);

  // Global identifier search: debounced server-side prefix queries over the
  // FULL scoped history whenever an identifier field has enough characters.
  // This is what makes "paste a call ID → see the call" work with no date
  // fiddling and no dependence on what's been loaded.
  useEffect(() => {
    if (access.kind !== 'ok') return;
    const minLen = RECORDINGS_GLOBAL_SEARCH_MIN_CHARS;
    const idQ = callId.trim().length >= minLen ? callId.trim() : '';
    const phoneQ = fromNumber.trim().length >= minLen ? fromNumber.trim() : '';
    const agentQ = elevated && agentQuery.trim().length >= minLen ? agentQuery.trim() : '';

    if (!idQ && !phoneQ && !agentQ) {
      searchSeqRef.current += 1; // invalidate any in-flight search
      setGlobalHits([]);
      setSearching(false);
      return;
    }

    const seq = ++searchSeqRef.current;
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const hits = await searchRecordingsGlobal({
          role: access.role,
          email: access.email,
          callId: idQ,
          fromNumber: phoneQ,
          agentQuery: agentQ,
        });
        if (searchSeqRef.current === seq) setGlobalHits(hits);
      } catch {
        if (searchSeqRef.current === seq) setGlobalHits([]);
      } finally {
        if (searchSeqRef.current === seq) setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [access, callId, fromNumber, agentQuery, elevated]);

  // Live filtering over the merged pool (browse window ∪ global hits). Every
  // active filter ANDs — text fields are case-insensitive partial matches,
  // dates apply client-side too so global hits respect an explicit range.
  // `hiddenByDates` powers the "clear dates to see N more" hint.
  const { visible, hiddenByDates } = useMemo(() => {
    const byId = new Map<string, Recording>();
    workingSet.forEach((r) => byId.set(r.id, r));
    globalHits.forEach((r) => !byId.has(r.id) && byId.set(r.id, r));

    const fromMs = dateInputToMs(dateFrom);
    const toMs = dateInputToMs(dateTo, true);
    const matches: Recording[] = [];
    let outOfRange = 0;
    byId.forEach((r) => {
      const textMatch =
        includesCI(r.call_id, callId) &&
        includesCI(r.from_number, fromNumber) &&
        includesCI(r.campaign, campaign) &&
        includesCI(r.disposition, disposition) &&
        (elevated ? includesCI(r.agent_email, agentQuery) : true) &&
        (direction ? r.direction === direction : true);
      if (!textMatch) return;
      const inRange =
        (fromMs === undefined || r.recorded_at >= fromMs) &&
        (toMs === undefined || r.recorded_at <= toMs);
      if (inRange) matches.push(r);
      else outOfRange += 1;
    });
    matches.sort((a, b) => b.recorded_at - a.recorded_at);
    return { visible: matches, hiddenByDates: outOfRange };
  }, [
    workingSet,
    globalHits,
    callId,
    fromNumber,
    campaign,
    disposition,
    agentQuery,
    direction,
    elevated,
    dateFrom,
    dateTo,
  ]);

  function clearFilters() {
    setCallId('');
    setDateFrom('');
    setDateTo('');
    setDirection('');
    setDisposition('');
    setCampaign('');
    setFromNumber('');
    setAgentQuery('');
  }

  async function play(rec: Recording) {
    setPlayBusyId(rec.id);
    try {
      const { url } = await requestPlaybackUrl(rec.id);
      setPlayingId(rec.id);
      setPlayUrl(url);
      setTimeout(() => {
        audioRef.current?.play().catch(() => {
          /* user can press play manually */
        });
      }, 0);
    } catch (e: any) {
      setErrorMsg(
        (e && typeof e.message === 'string' && e.message) ||
          'Could not start playback. The recording may have expired.',
      );
    } finally {
      setPlayBusyId(null);
    }
  }

  if (access.kind === 'loading') {
    return (
      <IonPage>
        <IonContent fullscreen>
          <div className="pm-app">
            <div className="pm-page" style={{ padding: '40px 28px' }}>
              <IonSpinner />
            </div>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  if (access.kind === 'denied') {
    return (
      <IonPage>
        <IonContent fullscreen>
          <div className="pm-app">
            <div className="pm-page" style={{ maxWidth: 640, padding: '40px 28px' }}>
              <div className="pm-header">
                <div className="pm-header-titles">
                  <h1>Recordings</h1>
                  <p>You don’t have access to the call-recordings portal. Ask an admin to grant you access.</p>
                </div>
              </div>
            </div>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  const cardStyle: React.CSSProperties = {
    background: 'var(--rr-paper, #fff)',
    border: '1px solid var(--pm-border, #d8d4cc)',
    borderRadius: 'var(--pm-radius-lg, 12px)',
    padding: '14px 16px',
  };
  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 10px',
    border: '1px solid var(--pm-border, #d8d4cc)',
    borderRadius: 8,
    background: '#fff',
    fontSize: 14,
  };
  const labelStyle: React.CSSProperties = { fontSize: 12, fontWeight: 600, marginBottom: 4, display: 'block' };

  return (
    <IonPage>
      <IonContent fullscreen>
        <div className="pm-app">
          <div className="pm-page" style={{ maxWidth: 1040, padding: '24px 28px' }}>
            <div
              className="pm-header"
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}
            >
              <div className="pm-header-titles">
                <h1>Call recordings</h1>
                <p>
                  {elevated
                    ? `Team view — ${RECORDINGS_ROLE_LABELS[access.role]}. Every playback is audit-logged.`
                    : 'The calls you handled. Only you can see these.'}
                </p>
              </div>
              {access.role === 'admin' && (
                <button
                  type="button"
                  className="pm-btn"
                  style={{ padding: '8px 16px', whiteSpace: 'nowrap' }}
                  onClick={() => history.push('/Recordings/Admin')}
                >
                  Manage access
                </button>
              )}
            </div>

            {/* ---- Live filters (type to search; no apply step) ---- */}
            <div style={{ ...cardStyle, marginTop: 16 }}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                  gap: 12,
                }}
              >
                <div>
                  <label style={labelStyle}>Call ID</label>
                  <input
                    style={inputStyle}
                    value={callId}
                    onChange={(e) => setCallId(e.target.value)}
                    placeholder="type or paste — searches everything"
                  />
                </div>
                <div>
                  <label style={labelStyle}>From date</label>
                  <input style={inputStyle} type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                </div>
                <div>
                  <label style={labelStyle}>To date</label>
                  <input style={inputStyle} type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                </div>
                <div>
                  <label style={labelStyle}>Direction</label>
                  <select style={inputStyle} value={direction} onChange={(e) => setDirection(e.target.value)}>
                    <option value="">Any</option>
                    <option value="inbound">Inbound</option>
                    <option value="outbound">Outbound</option>
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Campaign</label>
                  <input style={inputStyle} value={campaign} onChange={(e) => setCampaign(e.target.value)} placeholder="contains…" />
                </div>
                <div>
                  <label style={labelStyle}>Disposition</label>
                  <input style={inputStyle} value={disposition} onChange={(e) => setDisposition(e.target.value)} placeholder="contains…" />
                </div>
                <div>
                  <label style={labelStyle}>Caller # (ANI)</label>
                  <input style={inputStyle} value={fromNumber} onChange={(e) => setFromNumber(e.target.value)} placeholder="contains…" />
                </div>
                {elevated && (
                  <div>
                    <label style={labelStyle}>Agent</label>
                    <input
                      style={inputStyle}
                      value={agentQuery}
                      onChange={(e) => setAgentQuery(e.target.value)}
                      placeholder="email — searches everything"
                    />
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 12, marginTop: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <button type="button" onClick={clearFilters} className="pm-btn" style={{ padding: '8px 16px' }}>
                  Clear
                </button>
                <span style={{ fontSize: 12, color: '#888' }}>
                  {loadState === 'loading'
                    ? 'Loading…'
                    : `${visible.length} result${visible.length === 1 ? '' : 's'}${searching ? ' · searching all recordings…' : ''}`}
                </span>
                {hiddenByDates > 0 && !searching && (
                  <button
                    type="button"
                    onClick={() => {
                      setDateFrom('');
                      setDateTo('');
                    }}
                    style={{
                      fontSize: 12,
                      color: 'var(--rr-link, #2456A6)',
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      cursor: 'pointer',
                      textDecoration: 'underline',
                    }}
                  >
                    {hiddenByDates} more outside the selected dates — clear dates to show
                  </button>
                )}
              </div>
            </div>

            {errorMsg && (
              <div style={{ ...cardStyle, marginTop: 12, borderColor: '#C2433D', color: '#C2433D' }}>{errorMsg}</div>
            )}

            {/* ---- Inline player ---- */}
            {playUrl && (
              <div style={{ ...cardStyle, marginTop: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Now playing</div>
                <audio
                  ref={audioRef}
                  src={playUrl}
                  controls
                  controlsList="nodownload noplaybackrate"
                  style={{ width: '100%' }}
                />
              </div>
            )}

            {/* ---- List ---- */}
            <div style={{ ...cardStyle, marginTop: 12, padding: 0, overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                  <thead>
                    <tr style={{ textAlign: 'left', background: 'var(--rr-stone-100, #f4f2ee)' }}>
                      <Th>When</Th>
                      <Th>Call ID</Th>
                      <Th>Dir</Th>
                      <Th>Caller #</Th>
                      <Th>Campaign</Th>
                      <Th>Disposition</Th>
                      {elevated && <Th>Agent</Th>}
                      <Th sticky>Play</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((r) => (
                      <tr key={r.id} style={{ borderTop: '1px solid var(--pm-border, #eee)' }}>
                        <Td>{fmtWhen(r.recorded_at)}</Td>
                        <Td>{r.call_id || '—'}</Td>
                        <Td>{directionLabel(r.direction)}</Td>
                        <Td>
                          {r.from_number
                            ? maskPhone(r.from_number)
                            : /* withheld caller ID — show Five9's own label
                                 rather than an empty cell */
                              r.from_number_raw || '—'}
                        </Td>
                        <Td>{r.campaign || '—'}</Td>
                        <Td>{r.disposition || '—'}</Td>
                        {elevated && <Td>{r.agent_email || '—'}</Td>}
                        <Td sticky>
                          <button
                            className="pm-btn pm-btn-primary"
                            style={{ padding: '5px 12px' }}
                            disabled={playBusyId === r.id}
                            onClick={() => play(r)}
                          >
                            {playBusyId === r.id ? '…' : playingId === r.id ? 'Replay' : 'Play'}
                          </button>
                        </Td>
                      </tr>
                    ))}
                    {visible.length === 0 && loadState !== 'loading' && (
                      <tr>
                        <td colSpan={elevated ? 8 : 7} style={{ padding: 24, textAlign: 'center', color: '#888' }}>
                          {searching
                            ? 'Searching all recordings…'
                            : hiddenByDates > 0
                            ? 'No matches in the selected date range — clear the dates to search everything.'
                            : 'No recordings match your search.'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 14, minHeight: 40 }}>
              {loadState === 'loading' ? (
                <IonSpinner />
              ) : capped ? (
                <button
                  type="button"
                  className="pm-btn"
                  style={{ padding: '8px 20px' }}
                  disabled={loadingMore}
                  onClick={loadMore}
                >
                  {loadingMore ? 'Loading…' : 'Load older recordings'}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

// The action column is pinned to the right edge so the Play button stays in
// view no matter how wide the row scrolls; it floats over the scrolling cells.
const STICKY_TH: React.CSSProperties = {
  position: 'sticky',
  right: 0,
  background: 'var(--rr-stone-100, #f4f2ee)',
  zIndex: 2,
  boxShadow: '-6px 0 6px -6px rgba(0,0,0,0.18)',
};
const STICKY_TD: React.CSSProperties = {
  position: 'sticky',
  right: 0,
  background: 'var(--rr-paper, #fff)',
  zIndex: 1,
  boxShadow: '-6px 0 6px -6px rgba(0,0,0,0.12)',
};

const Th: React.FC<{ children: React.ReactNode; sticky?: boolean }> = ({ children, sticky }) => (
  <th style={{ padding: '10px 12px', fontWeight: 600, whiteSpace: 'nowrap', ...(sticky ? STICKY_TH : {}) }}>
    {children}
  </th>
);
const Td: React.FC<{ children: React.ReactNode; sticky?: boolean }> = ({ children, sticky }) => (
  <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', ...(sticky ? STICKY_TD : {}) }}>{children}</td>
);

export default Recordings;
